import React from 'react';
import { Outlet, NavLink, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { User, Users, HandHeart, ListChecks, Calendar, LogOut, Home, ShieldAlert } from 'lucide-react';
import Logo from '@/components/Logo';

export default function MemberLayout() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-moj-cream"><div className="text-moj-purple">Loading…</div></div>;
  }
  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-moj-cream">
        <div className="card-moj p-8 max-w-md text-center">
          <ShieldAlert size={48} className="text-moj-purple mx-auto mb-3"/>
          <h2 className="font-serif text-2xl text-moj-purple-deep mb-2">Please sign in</h2>
          <p className="text-moj-charcoal mb-4">You need to be signed in to access the member dashboard.</p>
          <Link to="/login" className="btn-primary">Members Login</Link>
        </div>
      </div>
    );
  }
  if (user.status !== 'approved') {
    navigate('/pending-approval');
    return null;
  }

  const links = [
    { to: '/member/dashboard', label: 'Overview', icon: Home },
    { to: '/member/profile', label: 'My Profile', icon: User },
    { to: '/member/family', label: 'Family Members', icon: Users },
    { to: '/member/volunteer', label: 'Volunteer Teams', icon: HandHeart },
    { to: '/member/tasks', label: 'My Tasks', icon: ListChecks },
    { to: '/member/rsvps', label: 'My RSVPs', icon: Calendar },
  ];

  return (
    <div className="min-h-screen bg-moj-cream">
      <header className="bg-white border-b-2 border-moj-gold/30 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between h-20">
          <Link to="/"><Logo size="md" /></Link>
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline text-sm text-moj-purple-deep">Welcome, <strong>{user.full_name || user.google_email}</strong></span>
            <button onClick={async () => { await logout(); navigate('/'); }} className="btn-outline text-sm py-2 px-4" data-testid="member-logout">
              <LogOut size={16}/> Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 grid md:grid-cols-[260px_1fr] gap-8">
        <aside className="md:sticky md:top-6 self-start">
          <nav className="card-moj p-4 space-y-1" aria-label="Member navigation">
            {links.map(({ to, label, icon: Icon }) => (
              <NavLink
                key={to}
                to={to}
                end
                className={({ isActive }) => `flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition ${isActive ? 'bg-moj-purple text-white' : 'text-moj-purple-deep hover:bg-moj-cream'}`}
              >
                <Icon size={18} /> {label}
              </NavLink>
            ))}
          </nav>
        </aside>
        <main>
          <Outlet />
        </main>
      </div>
    </div>
  );
}
